/**
 * Pure utility functions for field rendering and data conversion
 * No React dependencies - can be used in any context
 */

import type { SimpleFieldConfig, FieldValue, BaseEntity } from "../types";

/**
 * Helper function to check if a value is a UUID
 */
export const isUUID = (value: unknown): boolean => {
  if (typeof value !== "string") return false;
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
};

/**
 * Normalize multi-user field data to always return an array of user IDs
 * Handles: arrays, null, undefined, single strings, user objects with {id: "..."}
 */
export const normalizeMultiUserData = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item: unknown) => {
        if (typeof item === "string") return item;
        if (
          typeof item === "object" &&
          item !== null &&
          "id" in item &&
          typeof (item).id === "string"
        )
          return (item as { id: string }).id;
        return null;
      })
      .filter((id: string | null): id is string => id !== null);
  }
  if (typeof value === "string") return [value];
  if (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    typeof (value).id === "string"
  )
    return [(value as { id: string }).id];
  return [];
};

/**
 * Converts a field value to the appropriate type based on field configuration
 */
export const convertFieldValue = (
  value: string | FieldValue,
  fieldConfig: SimpleFieldConfig
): FieldValue => {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  switch (fieldConfig.type) {
    case "boolean":
      if (typeof value === "string") {
        return value === "true" || value === "1" || value === "yes" || value === "on";
      }
      return Boolean(value);

    case "number":
      if (typeof value === "string") {
        const num = parseFloat(value);
        return isNaN(num) ? 0 : num;
      }
      return typeof value === "number" ? value : 0;

    case "select":
    case "text":
    case "textarea":
    case "user":
    case "client-select":
    case "template-select":
      return String(value);

    case "multi-user":
    case "tags":
      if (Array.isArray(value)) {
        return value.map(String);
      }
      return [String(value)];

    case "date":
    case "datetime":
    case "time":
    case "due-date":
    case "date-worked":
      if (value instanceof Date) {
        return value.toISOString();
      }
      return String(value);

    default:
      return value;
  }
};

/**
 * Creates a properly typed update payload for inline editing
 */
export const createUpdatePayload = <T extends BaseEntity>(
  rowData: T,
  fieldId: string,
  value: FieldValue,
  fieldConfig: SimpleFieldConfig
): { id: string } & Record<string, FieldValue> => {
  const convertedValue = convertFieldValue(value, fieldConfig);

  return {
    id: rowData.id,
    [fieldId]: convertedValue,
  };
};
